import { Button, AppBar, Toolbar, Box } from "@mui/material";
import type { ReactNode } from "react";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { makeStyles } from "../../theme/theme";
import { MainLayout } from "../../layout/NewPageLayout";
import { NavBarItems } from "../../layout/NewNavBarItems";
import { clearUserImpersonation } from "../../store/actions/auth";
import type { RootState } from "../../store/state/rootReducer";
import type { AppDispatch } from "../../store/state/rootStore";
import { useRoleFlags } from "../../hooks/usePermissions";
import { OrgSwitcher } from "../OrgSwitcher";

import { OrganizationLogo } from "./OrganizationLogo";

// Dennis added this so that we have a view which quickly enables us to put all
// 'standard' styles side-by-side and compare them against any style guide doc that
// we might get from UX. Not sure if this is a good idea or if it ever will be
// as the mockups we are working from now are not very consistent with the guide we
// were given, but since the page already exists for now leaving it in
const SHOW_STYLE_GUIDE = false;

export interface INewLayoutProps {
  children: ReactNode;
}

const useStyles = makeStyles<{ isExpanded: boolean }>()((theme, { isExpanded }) => ({
  formLabel: {
    color: "inherit !important",
  },
  formControl: {
    backgroundColor: "inherit !important",
    flex: 1,
    "& .MuiSelect-select a": {
      color: "white",
    },
  },
  addGroup: {
    backgroundColor: "#E0F1EE",
    color: "#288E7F",
    padding: "10px 15px",
    cursor: "pointer",
    width: "auto",
  },
  groupSelectContainer: {
    marginBottom: isExpanded ? theme.spacing(6) : 0,
    display: "flex",
  },
  tooltip: {
    fontSize: "1em",
  },

  redbackground: {
    backgroundColor: "red",
  },
  justifyright: {
    justifyContent: "right",
  },
  switchbutton: {
    margin: "10px",
    color: "white",
  },

  options: {
    zIndex: "9999",
    width: "250px",
    overflow: "visible",
    margin: "0 auto",
    border: 0,
    borderRadius: "5px 5px 0 0",
    marginBottom: 20,
    "& .MuiPaper-root": {
      width: "250px",
    },
  },
  textField: {
    borderRadius: "5px",
    margin: "0 auto 48px",
    width: "180px",
    height: "50px",
    fontSize: "14px",

    "& .MuiSvgIcon-root": {
      color: "white",
    },
  },
  newGroup: {
    backgroundColor: "#E0F1EE",
    color: "#288E7F",
    padding: "10px 15px",
    cursor: "pointer",
    position: "fixed",
    width: "250px",
    bottom: "-20px",
    borderRadius: "0 0 5px 5px ",
  },
  filter: {
    padding: "5px 0",
    borderBottom: "2px solid #595959",
  },
  filterInput: {
    width: "100%",
    "& input": {
      padding: "5px 15px",
    },
    "& .MuiInput-underline:before": {
      borderBottom: "0",
    },
    "& .MuiInput-underline:after": {
      borderBottom: "0",
    },
    "& .MuiInput-underline:hover:before": {
      borderBottom: "0",
    },
  },
  filterIcon: {
    color: "#595959",
    position: "relative",
    right: "10%",
  },
  links: {
    width: "100%",
    padding: "5px 15px",
    textDecoration: "none",
    color: "#595959",
    "&:hover": {
      textDecoration: "none",
      color: "#595959",
    },
  },
  groupItem: {
    padding: "0",
  },

  selector: {
    "& .MuiList-root": {
      padding: 0,
    },
  },
}));

export function NewLayout({ children }: INewLayoutProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [isExpanded, setIsExpanded] = useState(true);
  const location = useLocation();
  const isDiscoverRoute = location.pathname.endsWith("/discover") || location.pathname.includes("/discover/");
  const wasAutoCollapsedByDiscover = useRef(false);

  useEffect(() => {
    if (isDiscoverRoute) {
      if (isExpanded) {
        wasAutoCollapsedByDiscover.current = true;
        setIsExpanded(false);
      }
    } else if (wasAutoCollapsedByDiscover.current) {
      wasAutoCollapsedByDiscover.current = false;
      setIsExpanded(true);
    }
  }, [isDiscoverRoute]);

  const isUserImpersonated = useSelector((state: RootState) => !!state.auth.superToken);
  const isFeatureFlagRemixerEnabled = useSelector((state: RootState) => state.featureFlags.flags?.enableRemixer);
  const isThemesEnabled = useSelector((state: RootState) => state.featureFlags.flags?.enableQualitativeCoding === true);

  const { classes } = useStyles({ isExpanded });

  // Note: Legacy => only admin & org_managers had the access
  const { isOrgManager, isAdmin } = useRoleFlags();
  const hasPermissions = isAdmin || isOrgManager;

  const nav = (
    <>
      <OrganizationLogo isExpanded={isExpanded} />
      <Box sx={{ px: isExpanded ? "14px" : "6px", mb: 1 }}>
        <OrgSwitcher isExpanded={isExpanded} />
      </Box>
      <NavBarItems
        alwaysShowChildren
        itemsById={{
          organizations: {
            id: "organizations",
            href: "/organizations",
            title: "Organizations",
            icon: "organizations",
            children: [],
            hidden: true,
          },
          projects: {
            id: "projects",
            href: hasPermissions ? "/projects" : "/",
            title: "Projects",
            icon: "projects",
            sectionTitle: "Content",
            children: [],
          },
          myInterviews: {
            id: "myInterviews",
            href: `/myInterviews`,
            title: "Interviews",
            icon: "myInterviews",
            children: [],
            hidden: false,
          },
          clips: {
            id: "clips",
            href: `/clips`,
            title: "Clips",
            icon: "clips",
            children: [],
            hidden: false,
          },
          themes: {
            id: "themes",
            href: `/themes`,
            title: "Themes",
            icon: "themes",
            children: [],
            hidden: !isThemesEnabled,
            beta: true,
          },
          remixer: {
            id: "remixer",
            href: `/remixes`,
            title: "Remixes",
            icon: "remixes",
            children: [],
            hidden: !isFeatureFlagRemixerEnabled,
            beta: true,
          },
          galleries: {
            id: "galleries",
            href: `/galleries`,
            title: "Galleries",
            icon: "galleries",
            sectionTitle: "Publishing",
            sectionDivider: true,
            children: [],
            beta: true,
          },
          portals: {
            id: "portals",
            href: `/portals`,
            title: "Portals",
            icon: "portals",
            children: [],
            hidden: false,
          },
        }}
        rootIds={[
          "interviews",
          "organizations",
          "projects",
          "manage",
          "myInterviews",
          "clips",
          "themes",
          "remixer",
          "galleries",
          "portals",
          ...(SHOW_STYLE_GUIDE && process.env.NODE_ENV === "development" ? ["dev_style_guide"] : []),
        ]}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      />
    </>
  );

  return (
    <>
      {isUserImpersonated && (
        <AppBar position="static" className={classes.redbackground}>
          <Toolbar className={classes.justifyright}>
            WARNING: You are impersonating another user!!!
            <Button
              variant="outlined"
              className={classes.switchbutton}
              onClick={() => dispatch(clearUserImpersonation())}
            >
              Return to Self
            </Button>
          </Toolbar>
        </AppBar>
      )}
      <MainLayout
        isExpanded={isExpanded}
        isUserImpersonated={isUserImpersonated}
        setExpanded={setIsExpanded}
        sidebarElement={nav}
      >
        {children}
      </MainLayout>
    </>
  );
}
